import { assertCalendarDate, calendarDate, compareCalendarDates, daysBetween, daysInMonth, firstDayOfYear, lastDayOfYear, parseCalendarDate, toEpochDay, } from '../date/index.js';
import { buildIsoWeekGrid } from './iso-week-grid.js';
/**
 * Minimum rendered bar width in CSS pixels (R10.4).
 *
 * A single-day Initiative would otherwise be a hairline in month orientation -
 * roughly one thirtieth of a column - and R10.4 requires it to stay individually
 * selectable. This is the one pixel quantity the domain owns, because the
 * requirement is stated in pixels; the scale it is applied against arrives from
 * the caller.
 */
export const MINIMUM_BAR_WIDTH_CSS_PX = 24;
/** Every {@link CalendarOrientation}, in the order R10.2 lists them. */
export const CALENDAR_ORIENTATIONS = [
    'month',
    'calendar-week',
    'sales-cycle',
];
/** Months in a year. Named because the month track's column count is exactly this. */
export const MONTHS_PER_YEAR = 12;
/**
 * Builds a validated {@link BarDateRange}.
 *
 * An end date before the start date is rejected rather than normalized: the hard
 * validation rule of R8.3 blocks such a save, so a range reaching the geometry
 * inverted is a defect upstream, and swapping the dates here would hide it
 * behind a plausible-looking bar.
 */
export function barDateRange(startDate, endDate) {
    assertCalendarDate(startDate);
    assertCalendarDate(endDate);
    if (compareCalendarDates(startDate, endDate) > 0) {
        throw new RangeError('bar date range ends before it starts');
    }
    return { startDate, endDate };
}
/**
 * The stored range of an Initiative whose dates arrive as ISO `YYYY-MM-DD` text
 * rather than as {@link CalendarDate} values.
 *
 * The conversion is `src/date/`'s single `parseCalendarDate`, not a parser of
 * this area's own: an Initiative's `startDate` and `endDate` live on the snapshot
 * as `PlainDate`, the same branded ten-character form, so the text a bar is drawn
 * from and the text a Sales_Cycle rule compares are validated by one function.
 * Callers already holding a `PlainDate` can convert with `toCalendarDate` and
 * call {@link barDateRange} directly; this overload exists for text arriving from
 * a query result that has not been branded yet.
 */
export function barDateRangeFromIsoStrings(startText, endText) {
    return barDateRange(parseCalendarDate(startText), parseCalendarDate(endText));
}
/**
 * The month-orientation track: 12 columns, 1 January through 31 December of
 * `planningYear` (R10.2).
 *
 * Column lengths vary with the month, so a February column holds 28 or 29 days
 * against January's 31 while both render at the same width.
 */
export function buildMonthTrack(planningYear) {
    const columns = [];
    for (let month = 1; month <= MONTHS_PER_YEAR; month += 1) {
        const dayCount = daysInMonth(planningYear, month);
        columns.push({
            index: month - 1,
            start: calendarDate(planningYear, month, 1),
            end: calendarDate(planningYear, month, dayCount),
            dayCount,
        });
    }
    const start = firstDayOfYear(planningYear);
    const end = lastDayOfYear(planningYear);
    return {
        orientation: 'month',
        planningYear,
        columns,
        start,
        end,
        dayCount: dayCountBetweenInclusive(start, end),
    };
}
/**
 * The calendar-week-orientation track for `planningYear`: the 52 or 53 full
 * seven-day ISO columns of R10.3.
 */
export function buildCalendarWeekTrack(planningYear) {
    return calendarWeekTrackFromGrid(buildIsoWeekGrid(planningYear));
}
/**
 * The calendar-week track of an ISO week grid a caller already holds.
 *
 * Derived from the grid rather than rebuilt, so the columns a bar is positioned
 * against are literally the columns the header renders.
 */
export function calendarWeekTrackFromGrid(grid) {
    return {
        orientation: 'calendar-week',
        planningYear: grid.planningYear,
        columns: grid.columns.map((column) => ({
            index: column.index,
            start: column.start,
            end: column.end,
            dayCount: dayCountBetweenInclusive(column.start, column.end),
        })),
        start: grid.start,
        end: grid.end,
        dayCount: dayCountBetweenInclusive(grid.start, grid.end),
    };
}
/**
 * Builds the Sales_Cycle track from the active cycles of `planningYear`.
 *
 * Filtering to active cycles of the year is the caller's job - `deriveSalesCycles`
 * in `src/salescycle/` already owns that predicate, and duplicating it here would
 * give the calendar a second opinion about which cycles exist.
 *
 * Ordering is by start date, then end date, then identifier, so a deterministic
 * column order survives two cycles that start on the same day.
 */
export function buildSalesCycleTrack(planningYear, cycles) {
    const ordered = [...cycles].sort((a, b) => compareCalendarDates(a.startDate, b.startDate) ||
        compareCalendarDates(a.endDate, b.endDate) ||
        (a.salesCycleId < b.salesCycleId ? -1 : a.salesCycleId > b.salesCycleId ? 1 : 0));
    const columns = ordered.map((cycle, index) => {
        if (compareCalendarDates(cycle.startDate, cycle.endDate) > 0) {
            throw new RangeError(`Sales_Cycle ${cycle.salesCycleId} ends before it starts`);
        }
        return {
            index,
            salesCycleId: cycle.salesCycleId,
            start: cycle.startDate,
            end: cycle.endDate,
        };
    });
    let start;
    let end;
    for (const column of columns) {
        if (start === undefined || compareCalendarDates(column.start, start) < 0)
            start = column.start;
        if (end === undefined || compareCalendarDates(column.end, end) > 0)
            end = column.end;
    }
    return { orientation: 'sales-cycle', planningYear, columns, start, end };
}
/** Whether either edge of the bar was clipped, so at least one continuation marker is due. */
export function barIsClipped(geometry) {
    return geometry.continuesBefore || geometry.continuesAfter;
}
/**
 * The bar for `range` on a day-proportional track, or `undefined` when the stored
 * range lies entirely outside the track.
 *
 * `undefined` rather than a zero-width bar: R10.16 forbids a placeholder or a
 * reserved slot for an Initiative that is not rendered, and a zero-width bar
 * would additionally be unselectable, which R10.4 forbids.
 */
export function barGeometryOnTrack(track, range) {
    const { startDate, endDate } = barDateRange(range.startDate, range.endDate);
    if (compareCalendarDates(endDate, track.start) < 0 ||
        compareCalendarDates(startDate, track.end) > 0) {
        return undefined;
    }
    const renderedStartDate = compareCalendarDates(startDate, track.start) < 0 ? track.start : startDate;
    const renderedEndDate = compareCalendarDates(endDate, track.end) > 0 ? track.end : endDate;
    const startColumns = trackPosition(track, renderedStartDate, 'left');
    const endColumns = trackPosition(track, renderedEndDate, 'right');
    return {
        orientation: track.orientation,
        columnCount: track.columns.length,
        startColumns,
        endColumns,
        widthColumns: endColumns - startColumns,
        renderedStartDate,
        renderedEndDate,
        renderedDayCount: dayCountBetweenInclusive(renderedStartDate, renderedEndDate),
        storedStartDate: startDate,
        storedEndDate: endDate,
        continuesBefore: compareCalendarDates(startDate, renderedStartDate) < 0,
        continuesAfter: compareCalendarDates(endDate, renderedEndDate) > 0,
    };
}
/**
 * The bar for `range` in month orientation for `planningYear`, positioned
 * day-proportionally within each month column (R10.2, R10.4).
 */
export function monthBarGeometry(planningYear, range) {
    return barGeometryOnTrack(buildMonthTrack(planningYear), range);
}
/**
 * The bar for `range` in calendar-week orientation for `planningYear`.
 *
 * Clipping is against the ISO grid bounds, so 1-3 January of a year whose week 1
 * starts on the 4th are outside this grid and are rendered by the previous year's
 * grid instead. See the module comment.
 */
export function calendarWeekBarGeometry(planningYear, range) {
    return barGeometryOnTrack(buildCalendarWeekTrack(planningYear), range);
}
/**
 * The bar for an Initiative in Sales_Cycle orientation: exactly one bar spanning
 * from the earliest to the latest assigned Sales_Cycle column of the selected
 * planning year (R7.10), or `undefined` when the Initiative is assigned to no
 * cycle of that year and therefore has no bar to draw.
 *
 * Column-aligned, not day-proportional: R7.10 defines the span in terms of the
 * assigned *cycles*, and the columns of this orientation need be neither
 * contiguous nor equal in length. The continuation markers still follow the same
 * rule as everywhere else - they say the Initiative's stored range reaches beyond
 * the rendered edge, which in this orientation happens both when the Initiative
 * leaves the planning year and when a manual override (R7.4) assigns a cycle set
 * narrower than the Initiative's own dates.
 *
 * Identifiers not present in the track are ignored rather than rejected: an
 * assignment may name cycles of neighbouring planning years (R7.1), and those
 * belong to those years' tracks.
 */
export function salesCycleBarGeometry(track, assignedSalesCycleIds, range) {
    const { startDate, endDate } = barDateRange(range.startDate, range.endDate);
    const assigned = new Set(assignedSalesCycleIds);
    const columns = track.columns.filter((column) => assigned.has(column.salesCycleId));
    const first = columns[0];
    const last = columns[columns.length - 1];
    if (first === undefined || last === undefined) {
        return undefined;
    }
    // Columns are in date order, but two of them may overlap, so the latest end is
    // not necessarily the last column's end.
    let renderedEndDate = last.end;
    for (const column of columns) {
        if (compareCalendarDates(column.end, renderedEndDate) > 0)
            renderedEndDate = column.end;
    }
    const renderedStartDate = first.start;
    return {
        orientation: 'sales-cycle',
        columnCount: track.columns.length,
        startColumns: first.index,
        endColumns: last.index + 1,
        widthColumns: last.index + 1 - first.index,
        renderedStartDate,
        renderedEndDate,
        renderedDayCount: dayCountBetweenInclusive(renderedStartDate, renderedEndDate),
        storedStartDate: startDate,
        storedEndDate: endDate,
        continuesBefore: compareCalendarDates(startDate, renderedStartDate) < 0,
        continuesAfter: compareCalendarDates(endDate, renderedEndDate) > 0,
    };
}
/**
 * The bar's pixel extent at `columnWidthPx`, widened to the R10.4 minimum where
 * the date extent would render narrower.
 *
 * The column width comes from the caller because the domain has no viewport: the
 * same geometry renders at any width, and task 7.5 assigns lanes on the pixel
 * interval this returns - which is the reason the minimum width matters to lane
 * assignment at all, since two same-day bars overlap visually while their date
 * intervals are point-equal.
 *
 * Widening keeps the left edge at the start date (R10.4) and extends to the
 * right, except at the track's right edge, where it shifts left instead so the
 * bar stays inside the rendered year.
 */
export function toBarPixelInterval(geometry, columnWidthPx, minimumWidthPx = MINIMUM_BAR_WIDTH_CSS_PX) {
    if (!Number.isFinite(columnWidthPx) || columnWidthPx <= 0) {
        throw new RangeError(`column width must be a positive number, received ${String(columnWidthPx)}`);
    }
    if (!Number.isFinite(minimumWidthPx) || minimumWidthPx < 0) {
        throw new RangeError(`minimum width must be a non-negative number, received ${String(minimumWidthPx)}`);
    }
    const trackWidthPx = geometry.columnCount * columnWidthPx;
    const naturalLeftPx = geometry.startColumns * columnWidthPx;
    const naturalRightPx = geometry.endColumns * columnWidthPx;
    const naturalWidthPx = naturalRightPx - naturalLeftPx;
    if (naturalWidthPx >= minimumWidthPx) {
        return {
            leftPx: naturalLeftPx,
            rightPx: naturalRightPx,
            widthPx: naturalWidthPx,
            widenedToMinimum: false,
        };
    }
    // A minimum wider than the whole track is degenerate; fill the track rather
    // than render outside it.
    const widthPx = Math.min(minimumWidthPx, trackWidthPx);
    const rightPx = Math.min(naturalLeftPx + widthPx, trackWidthPx);
    return { leftPx: rightPx - widthPx, rightPx, widthPx, widenedToMinimum: true };
}
/**
 * The position of one edge of `date` on `track`, in column units.
 *
 * `'left'` returns the position of the start of that day, `'right'` the position
 * of its end, which is what makes an inclusive end date occupy its whole day.
 * The date must lie inside the track; callers clip first.
 */
function trackPosition(track, date, edge) {
    const epochDay = toEpochDay(date);
    for (const column of track.columns) {
        const columnStart = toEpochDay(column.start);
        if (epochDay >= columnStart && epochDay <= toEpochDay(column.end)) {
            const dayOffset = epochDay - columnStart + (edge === 'right' ? 1 : 0);
            return column.index + dayOffset / column.dayCount;
        }
    }
    // Unreachable for a clipped date: a track's columns cover every day between its
    // bounds. Guarded so a future non-contiguous track fails loudly here rather
    // than silently drawing at position zero.
    throw new RangeError(`date ${String(date.year)}-${String(date.month)}-${String(date.day)} is not on the track`);
}
/**
 * Days from `from` through `to` inclusive, which is how both a track column and a
 * rendered bar measure their length: a one-day span is one day, not zero.
 */
function dayCountBetweenInclusive(from, to) {
    return daysBetween(from, to) + 1;
}
//# sourceMappingURL=bar-geometry.js.map